import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SubscriptionData {
  subscription_id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  plan_id: 'free' | 'pro';
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'expired' | 'grace_period' | 'on_hold' | 'paused';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
  payment_provider: 'stripe' | 'apple' | 'google' | null;
  billing_period?: 'monthly' | 'annual';
}

export interface SubscriptionPlan {
  plan_id: string;
  name: string;
  price: number;
  billing_period: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  features: Record<string, any>;
  is_active: boolean;
}

export interface UserTier {
  subscription_tier: 'free' | 'pro';
}

export function useSubscription(userId?: string) {
  const queryClient = useQueryClient();

  // Fetch user's current subscription
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('[Subscription] Error fetching subscription:', error);
        throw error;
      }

      return data as SubscriptionData | null;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  // Fetch user's subscription tier from users table
  const { data: userTier, isLoading: isLoadingTier } = useQuery({
    queryKey: ['user-tier', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('users')
        .select('subscription_tier')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('[Subscription] Error fetching user tier:', error);
        throw error;
      }

      return data as UserTier;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  // Fetch all available subscription plans
  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_monthly_pence', { ascending: true });

      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      return data as SubscriptionPlan[];
    },
    staleTime: 60 * 60 * 1000, // 1 hour - plans rarely change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  // Create checkout session mutation
  const createCheckout = useMutation({
    mutationFn: async ({ platform = 'web', billing_period }: { platform?: 'web' | 'mobile', billing_period?: 'monthly' | 'annual' } = {}) => {
      const startTime = Date.now();
      console.log('[Checkout] Starting checkout creation at:', new Date().toISOString());

      // Get fresh session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        console.error('[Checkout] Session error:', sessionError);
        throw new Error('Not authenticated');
      }

      console.log('[Checkout] Got session, elapsed:', `${Date.now() - startTime}ms`);

      // Build redirect URL based on current location (supports localhost and production)
      // Detect if user is on filmmaker or client dashboard
      const isFilmmaker = window.location.pathname.includes('/filmmaker/');
      const isClient = window.location.pathname.includes('/client/');

      let dashboardPath = '/dashboard';
      if (isFilmmaker) {
        dashboardPath = '/filmmaker/dashboard';
      } else if (isClient) {
        dashboardPath = '/client/dashboard';
      }

      const redirectUrl = `${window.location.origin}${dashboardPath}`;

      // Use test mode for localhost, live mode for production
      const isTestMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      console.log('[Checkout] Request details:', {
        platform,
        redirectUrl,
        isTestMode,
        billing_period,
        elapsed: `${Date.now() - startTime}ms`
      });

      const fetchStartTime = Date.now();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ platform, redirectUrl, test: isTestMode, billing_period }),
        }
      );

      const fetchDuration = Date.now() - fetchStartTime;
      console.log('[Checkout] Edge function response received in:', `${fetchDuration}ms`, { status: response.status });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Checkout] Checkout error:', response.status, error);
        throw new Error(error.error || `Failed to create checkout session (${response.status})`);
      }

      const data = await response.json();
      console.log('[Checkout] Checkout session created successfully:', {
        sessionId: data.sessionId,
        hasUrl: !!data.url,
        totalDuration: `${Date.now() - startTime}ms`
      });

      return data;
    },
    onSuccess: (data) => {
      console.log('[Checkout] Redirecting to Stripe checkout:', data.url);
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      console.error('[Checkout] Checkout failed:', error);
      toast.error('Failed to start checkout', {
        description: error.message,
      });
    },
  });

  // Create customer portal session mutation
  const createPortal = useMutation({
    mutationFn: async (platform: 'web' | 'mobile' = 'web') => {
      // Get fresh session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        console.error('Session error:', sessionError);
        throw new Error('Not authenticated');
      }

      // Build redirect URL based on current location (supports localhost and production)
      // Detect if user is on filmmaker or client dashboard
      const isFilmmaker = window.location.pathname.includes('/filmmaker/');
      const isClient = window.location.pathname.includes('/client/');

      let dashboardPath = '/dashboard';
      if (isFilmmaker) {
        dashboardPath = '/filmmaker/dashboard';
      } else if (isClient) {
        dashboardPath = '/client/dashboard';
      }

      const redirectUrl = `${window.location.origin}${dashboardPath}`;

      // Use test mode for localhost, live mode for production
      const isTestMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-customer-portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ platform, redirectUrl, test: isTestMode }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Portal error:', response.status, error);
        throw new Error(error.error || `Failed to create portal session (${response.status})`);
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Redirect to Stripe customer portal
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to open customer portal', {
        description: error.message,
      });
    },
  });

  // Refresh subscription data
  const refreshSubscription = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', userId] });
    queryClient.invalidateQueries({ queryKey: ['user-tier', userId] });
  };

  const hookState = {
    subscription,
    userTier: userTier?.subscription_tier || 'free',
    plans,
    isLoading: isLoadingSubscription || isLoadingTier || isLoadingPlans,
    createCheckout,
    createPortal,
    refreshSubscription,
    isPro: userTier?.subscription_tier === 'pro',
    isActive: subscription?.status === 'active' || subscription?.status === 'trialing',
  };

  return hookState;
}
